import { BufferAttribute, BufferGeometry } from 'three';

import { isBufferAttribute } from '../utils/index.js';

import { OutputType } from './SilhouetteGenerator.js';

const NAME = 'SilhouetteGeneratorWorker';
export class SilhouetteGeneratorWorker {
  running: boolean;
  worker: Worker | null;
  constructor(url: string) {
    this.running = false;
    this.worker = new Worker(url, {
      type: 'module',
    });
    this.worker.onerror = (e) => {
      if (e.message) {
        throw new Error(`${NAME}: Could not create Web Worker with error "${e.message}"`);
      } else {
        throw new Error(`${NAME}: Could not create Web Worker.`);
      }
    };
  }

  generate(
    geometry: BufferGeometry,
    options?: {
      output?: number;
      sortTriangles?: boolean;
      onProgress?: (progress: number) => void;
    }
  ) {
    const { output, sortTriangles, onProgress } = { ...options };
    if (this.running) {
      throw new Error(`${NAME}: Already running job.`);
    }

    if (this.worker === null) {
      throw new Error(`${NAME}: Worker has been disposed.`);
    }

    const { worker } = this;
    this.running = true;

    return new Promise<BufferGeometry[] | BufferGeometry>((resolve, reject) => {
      worker.onerror = (e) => {
        reject(new Error(`${NAME}: ${e.message}`));
        this.running = false;
      };

      worker.onmessage = (
        e: MessageEvent<{
          error?: string;
          result?:
            | {
                position: Float32Array;
                index: Float32Array;
              }[]
            | {
                position: Float32Array;
                index: Float32Array;
              };
          progress: number;
        }>
      ) => {
        this.running = false;
        const { data } = e;

        if (data.error) {
          reject(new Error(data.error));
          worker.onmessage = null;
        } else if (data.result) {
          if (output === OutputType.Both) {
            const result = (
              data.result as {
                position: Float32Array;
                index: Float32Array;
              }[]
            ).map((info) => {
              const geometry = new BufferGeometry();
              geometry.setAttribute('position', new BufferAttribute(info.position, 3, false));
              if (info.index) {
                geometry.setIndex(new BufferAttribute(info.index, 1, false));
              }

              return geometry;
            });

            resolve(result);
          } else {
            const result = data.result as {
              position: Float32Array;
              index: Float32Array;
            };
            const geometry = new BufferGeometry();
            geometry.setAttribute('position', new BufferAttribute(result.position, 3, false));
            geometry.setIndex(new BufferAttribute(result.index, 1, false));
            resolve(geometry);
          }

          worker.onmessage = null;
        } else if (onProgress) {
          onProgress(data.progress);
        }
      };

      if (!isBufferAttribute(geometry.attributes.position)) {
        throw new Error('SilhouetteGeneratorWorker: Position attribute is not a BufferAttribute.');
      }

      const index = geometry.index ? (geometry.index.array as Float32Array).slice() : null;
      const position = (geometry.attributes.position.array as Float32Array).slice();
      const transfer = [position.buffer];
      if (index) {
        transfer.push(index.buffer);
      }

      worker.postMessage(
        {
          index,
          position,
          options: {
            output,
            sortTriangles,
            onProgress: null,
            includedProgressCallback: Boolean(onProgress),
          },
        },
        transfer
      );
    });
  }

  dispose() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}
